import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import CinemaIcon from "../../components/cinema/CinemaIcon";

export default function LiveCategorySelect({
  label,
  categories,
  value,
  disabled,
  onChange
}) {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [placement, setPlacement] = useState({ above: false, height: 340 });
  const root = useRef(null);
  const trigger = useRef(null);
  const list = useRef(null);
  const search = useRef({ text: "", time: 0 });
  const selectedIndex = categories.findIndex(
    (category) => category.id === value
  );
  const isOpen = open && !disabled && categories.length > 0;

  useEffect(() => {
    setOpen(false);
  }, [value, disabled, categories]);

  useEffect(() => {
    if (!isOpen) return;
    const dismiss = (event) => {
      if (!root.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", dismiss);
    return () => document.removeEventListener("pointerdown", dismiss);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    const place = () => {
      const bounds = trigger.current.getBoundingClientRect();
      const below = window.innerHeight - bounds.bottom - 16;
      const above = bounds.top - 16;
      const flip = below < 240 && above > below;
      setPlacement({
        above: flip,
        height: Math.max(0, Math.min(340, flip ? above : below))
      });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || !list.current) return;
    const option = list.current.children[activeIndex];
    if (!option) return;
    const top = option.offsetTop;
    const bottom = top + option.offsetHeight;
    if (top < list.current.scrollTop) list.current.scrollTop = top;
    else if (bottom > list.current.scrollTop + list.current.clientHeight)
      list.current.scrollTop = bottom - list.current.clientHeight;
  }, [activeIndex, isOpen, placement.height]);

  const show = (index = Math.max(0, selectedIndex)) => {
    search.current = { text: "", time: 0 };
    setActiveIndex(index);
    setOpen(true);
  };
  const choose = (index) => {
    if (categories[index]) onChange(categories[index]);
    setOpen(false);
    trigger.current.focus();
  };
  const onKeyDown = (event) => {
    if (disabled || !categories.length) return;
    const { key } = event;
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(key)) {
      event.preventDefault();
      event.stopPropagation();
      if (key === "Home") show(0);
      else if (key === "End") show(categories.length - 1);
      else if (!isOpen) show();
      else
        setActiveIndex((index) =>
          Math.max(
            0,
            Math.min(
              categories.length - 1,
              index + (key === "ArrowDown" ? 1 : -1)
            )
          )
        );
    } else if (key === "Enter" || key === " ") {
      event.preventDefault();
      event.stopPropagation();
      if (isOpen) choose(activeIndex);
      else show();
    } else if (key === "Escape" && isOpen) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
    } else if (key === "Tab") {
      setOpen(false);
    } else if (
      key.length === 1 &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey
    ) {
      event.preventDefault();
      event.stopPropagation();
      const now = Date.now();
      const text =
        (now - search.current.time < 700 ? search.current.text : "") + key;
      search.current = { text, time: now };
      const query = (/^(.)\1*$/.test(text) ? key : text).toLocaleLowerCase();
      const start = isOpen ? activeIndex : Math.max(0, selectedIndex);
      const index = categories.findIndex((_, offset) =>
        categories[(start + offset + 1) % categories.length].name
          .toLocaleLowerCase()
          .startsWith(query)
      );
      if (index >= 0) {
        setActiveIndex((start + index + 1) % categories.length);
        setOpen(true);
      }
    }
  };

  return (
    <div
      className="live-genre-select"
      ref={root}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
    >
      <span id="live-category-label">{label}</span>
      <div className="live-category-control">
        <button
          ref={trigger}
          type="button"
          role="combobox"
          className="live-category-trigger"
          aria-labelledby="live-category-label"
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          aria-controls={isOpen ? "live-category-options" : undefined}
          aria-activedescendant={
            isOpen ? `live-category-option-${activeIndex}` : undefined
          }
          disabled={disabled || !categories.length}
          onClick={() => (isOpen ? setOpen(false) : show())}
          onKeyDown={onKeyDown}
        >
          <CinemaIcon name="sliders" />
          <span>{categories[selectedIndex]?.name || "—"}</span>
          <CinemaIcon name="down" className="live-category-chevron" />
        </button>
        {isOpen && (
          <ul
            id="live-category-options"
            ref={list}
            role="listbox"
            aria-labelledby="live-category-label"
            className={`live-category-options${
              placement.above ? " opens-above" : ""
            }`}
            style={{ maxHeight: placement.height }}
            onMouseDown={(event) => event.preventDefault()}
          >
            {categories.map((category, index) => (
              <li
                id={`live-category-option-${index}`}
                key={category.id}
                role="option"
                aria-selected={category.id === value}
                className={index === activeIndex ? "is-active" : undefined}
                onClick={() => choose(index)}
              >
                <span>{category.name}</span>
                {category.id === value && <CinemaIcon name="check" />}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
